import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import ComboBox from "../ComboBox";
import TextBox from "../TextBox";
import useTickets from "../../../hooks/useTickets";
import Button from "../Button";
import { PAGE_STATE, langComponent } from "../../../constants";
import AppContext from "../../../AppContext";
import useChat from "../../../hooks/useChat";
import Chat from ".";
import ChatSessionContext from "../../../contexts/ChatSessionContext";
import { SocketContext } from "../../../contexts/SocketContext";

const PAGE = {
  INFO: 1,
  TICKET: 2,
  QUEUE: 3,
  CHAT: 4,
};

const _options = [
  {
    name: "Panoramas",
    value: 1,
  },
  {
    name: "Work Exchanges",
    value: 2,
  },
  {
    name: "Sharing Projects",
    value: 3,
  },
  {
    name: "Purchasing",
    value: 4,
  },
  {
    name: "Other",
    value: 5,
  },
];

const SupportTicket = () => {
  const [page, setPage] = useState(PAGE.INFO);
  const { createTicket } = useTickets();
  const {
    getSession,
    createSession,
    querySessions,
    closeSession,
    createChat,
    CHAT_TYPE,
  } = useChat();
  const [ticket, setTicket] = useState(undefined);
  const [queuePosition, setQueuePosition] = useState(undefined);
  const { user, viewTicket, getLang } = useContext(AppContext);
  const { currentSession, setCurrentSession, getCurrentSession } =
    useContext(ChatSessionContext);
  const Translation = getLang(langComponent.chat);
  const [isLeavingQueue, setIsLeavingQueue] = useState(false);
  const intervalRef = useRef(null);
  const { socket } = useContext(SocketContext);

  useEffect(() => {
    init();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      closeSocketSupport();
    };
  }, []);

  useEffect(() => {
    if (currentSession) openSocketSupport();
    if (!currentSession) closeSocketSupport();
  }, [currentSession]);

  const _intervalCheckSupportChat = async () => {
    // * Check if have open chat
    const { data: currentSessions } = await querySessions({
      users: {
        $in: [user._id],
      },
      support: true,
      closed: false,
      staff: {
        $ne: null,
      },
    });

    if (currentSessions.length === 0) {
      return;
    }

    clearInterval(intervalRef.current);
    intervalRef.current = undefined;

    setCurrentSession(currentSessions[0]._id);
    setPage(PAGE.CHAT);
  };

  useEffect(() => {
    if (page !== PAGE.QUEUE || !queuePosition) {
      return;
    }

    // * Check if session is booked
    _clearInterval();

    intervalRef.current = setInterval(_intervalCheckSupportChat, 2000);

    return () => {
      _clearInterval();
    };
  }, [page, queuePosition]);

  const openSocketSupport = () => {
    // * Open socket support
    socket.emit(
      "message",
      JSON.stringify({
        event: "support-chat:join",
      })
    );
    socket.on("support-chat:on-new-session", handleIncomingNewSupportSession);
    socket.on("support-chat:on-session-changed", handleIncomingChangeSession);
  };

  const closeSocketSupport = () => {
    if (!socket) return;

    // * Close socket support
    socket.emit(
      "message",
      JSON.stringify({
        event: "support-chat:leave",
      })
    );
    socket.off("support-chat:on-new-session", handleIncomingNewSupportSession);
    socket.off("support-chat:on-session-changed", handleIncomingChangeSession);
  };

  const handleIncomingNewSupportSession = async (msg) => {
    const message = JSON.parse(msg);
    //TODO How to handleIncomingNewSupportSession
  };

  const handleIncomingChangeSession = async (msg) => {
    // const session = JSON.parse(msg);
    // // * If the changed session is current then switch to Chat
    // if (currentSession._id === session._id && session.staff) {
    //   setCurrentSession(session._id);
    //   setPage(PAGE.CHAT);
    //   return;
    // }
    // // * Else refresh to get position remaining
    // init();
  };

  const _clearInterval = () => {
    if (!intervalRef.current) return;

    clearInterval(intervalRef.current);

    intervalRef.current = undefined;
  };

  const init = async () => {
    // * Check if currently in queue
    const { data: openSessions } = await querySessions({
      users: {
        $in: [user._id],
      },
      support: true,
      staff: null,
      closed: false,
    });

    // * Already have open session with support
    if (openSessions.length > 0) {
      handleChatQueue(openSessions[0]);
      return;
    }

    // * Check if have open chat
    const { data: currentSessions } = await querySessions({
      users: {
        $in: [user._id],
      },
      support: true,
      closed: false,
      staff: {
        $ne: null,
      },
    });

    if (currentSessions.length > 0) {
      setCurrentSession(currentSessions[0]._id);
      setPage(PAGE.CHAT);
    }
  };

  const handleSupportValidate = ({ category, topic, desc }) => {
    let errors = {};
    if (!category) errors.category = Translation("Please specify a category");
    if (!topic) errors.topic = Translation("Please specify topic to discuss");
    if (!desc) errors.desc = Translation("Please specify description");

    return errors;
  };

  const handleSupportSubmit = async (values, options) => {
    const { category, topic, desc } = values;

    const currentHour = new Date().getHours();

    // FIXME: Add back to support session
    const _session = await createSession([user._id], true);

    // * Create new message with the topic
    await createChat(_session._id, CHAT_TYPE.MESSAGE, {
      msg: `Category: ${
        _options.find((o) => o.value === category)?.name
      }\nTopic: ${topic}\nDesc: ${desc}`,
    });

    handleChatQueue(_session._id);
    return;

    if (currentHour >= 9 && currentHour < 18) {
    }

    options.setSubmitting(false);

    // TODO: Check if is out of office hours
    createTicket({ category, topic, desc })
      .then((res) => {
        setPage(PAGE.TICKET);
        setTicket(res);
      })
      .catch((error) => {
        alert(error.message);
      });
  };

  const handleChatQueue = async (session) => {
    // * Create new session of use current one
    let _session = session;

    // * ==== Get position in queue ====
    // * Get number of support sessions without staffs up until new session
    const { data: sessionsBefore } = await querySessions(
      {
        support: true,
        active: true,
        deleted: false,
        staff: null,
        closed: false,
        createdOn: {
          $lt: new Date(_session.createdOn),
        },
      },
      null,
      -1,
      {
        _id: 1,
      }
    );

    setPage(PAGE.QUEUE);
    setQueuePosition(sessionsBefore.length + 1);
    setCurrentSession(_session._id);
  };

  const handleLeaveQueueClick = async () => {
    setIsLeavingQueue(true);

    try {
      // * Check if have open chat
      const { data: currentSessions } = await querySessions({
        users: {
          $in: [user._id],
        },
        support: true,
        closed: false,
        staff: null,
      });

      if (currentSessions.length > 0) {
        await closeSession(currentSessions[0]._id);
      }

      setCurrentSession(undefined);
      setQueuePosition(undefined);
      setPage(PAGE.INFO);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLeavingQueue(false);
    }
  };

  const handleViewTicketClick = () => {
    viewTicket(ticket._id);
  };

  const handleOnChatReturn = () => {
    setPage(PAGE.INFO);
    setCurrentSession(undefined);
    init();
  };

  if (page === PAGE.TICKET && ticket) {
    return (
      <div className="chat-sessions-support">
        <div className="chat-sessions-support-ticket">
          <span className="chat-sessions-support-ticket__title">
            🎉
            <br />
            {Translation("Ticket created")}
          </span>
          <span
            className="chat-sessions-support-ticket__link"
            onClick={handleViewTicketClick}
          >
            {Translation("View your ticket")}
          </span>
        </div>
      </div>
    );
  }

  if (page === PAGE.QUEUE && queuePosition) {
    return (
      <div className="chat-sessions-support">
        <div className="chat-sessions-support-queue">
          <span className="chat-sessions-support-queue__h1">
            {Translation("Please patiently wait for our support")}
          </span>
          <span className="chat-sessions-support-queue__h2">
            {Translation("Thank you for waiting")}
          </span>
          <span className="chat-sessions-support-queue__sub">
            {Translation("Here's your position in the queue")}
          </span>
          <div className="chat-sessions-support-queue__position">
            {queuePosition}
          </div>
          <span className="chat-sessions-support-queue__footer">
            {Translation("We'll notify you when we're ready")}
          </span>
          <Button
            label={Translation("Close request & leave queue")}
            variant="secondary"
            onClick={handleLeaveQueueClick}
            disabled={isLeavingQueue}
          />
        </div>
      </div>
    );
  }

  if (page === PAGE.CHAT && currentSession) {
    return (
      <div className="chat-sessions-support">
        <Chat
          mini={true}
          sessionId={currentSession._id}
          userId={user._id}
          onReturn={handleOnChatReturn}
        />
      </div>
    );
  }

  return (
    <div className="chat-sessions-support">
      <h3 className="chat-sessions-support__header">
        {Translation("Start a discussion")}
      </h3>
      <Formik
        initialValues={{ category: "", topic: "", desc: "" }}
        validate={handleSupportValidate}
        onSubmit={handleSupportSubmit}
      >
        <Form className="chat-sessions-support-content">
          <ComboBox
            label={Translation("Category")}
            placeholder={Translation("Select category")}
            name="category"
            options={[
              {
                name: "Panoramas",
                value: 1,
              },
              {
                name: "Work Exchanges",
                value: 2,
              },
              {
                name: "Sharing Projects",
                value: 3,
              },
              {
                name: "Purchasing",
                value: 4,
              },
              {
                name: "Other",
                value: 5,
              },
            ]}
          />
          <TextBox
            label={Translation("Topic")}
            name="topic"
            placeholder={Translation("Specify topic to discuss")}
          />
          <TextBox
            name="desc"
            label={Translation("Description")}
            placeholder={Translation("Give use an in-detail of you issue")}
          />
          <span className="chat-sessions-support-content__desc">
            {Translation(
              "*Support chat is available only during office hours."
            )}
            <br />
            {Translation("Office hours (Everyday): 09:00 - 18:00")}
            <br />
            <br />
            {Translation(
              "Out of office hours or holdays will be created as support tickets instead"
            )}
          </span>
          <Button label={Translation("Request")} type="submit" />
        </Form>
      </Formik>
    </div>
  );
};

SupportTicket.propTypes = {};

SupportTicket.defaultProps = {};

export default SupportTicket;
